import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./map-hud.css";

const STORAGE_KEY = "hud.resourcesCoachSeen";

type Props = {
  /**
   * True once the resources modal is open. That means the user found the
   * feature on their own, so the mark is retired for good rather than merely
   * hidden — otherwise closing the modal would re-arm the timer and the
   * tutorial would pop up *after* they had already used it.
   */
  disabled?: boolean;
  onDismiss?: () => void;
};

/**
 * First-run coach mark for the top-bar resource strip.
 *
 * Deliberately standalone rather than a react-joyride step: the shared
 * `CustomTooltip` in components/Tour/GameTour.tsx renders only an arrow and
 * drops `step.content`, and ArenaTour clears the redux tour slice on every
 * visit to /home — which is exactly the route this mark lives on. A plain
 * localStorage flag is the only thing that survives that.
 */
const markSeen = () => {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    /* private mode — the mark simply reappears next session */
  }
};

const ResourceCoachMark = ({ disabled, onDismiss }: Props) => {
  const { t } = useAppTranslation("common");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (disabled) {
      setVisible(false);
      markSeen();
      return;
    }
    let seen = false;
    try {
      seen = localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      // private mode / storage disabled — treat as already seen so we never
      // trap the user behind an overlay we cannot remember dismissing.
      seen = true;
    }
    if (seen) return;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [disabled]);

  const dismiss = useCallback(() => {
    setVisible(false);
    markSeen();
    onDismiss?.();
  }, [onDismiss]);

  useEffect(() => {
    if (!visible) return;
    // Any interaction anywhere counts as "click anywhere", matching the copy.
    const handler = () => dismiss();
    window.addEventListener("pointerdown", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, [visible, dismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="mht-coach"
          role="status"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.25 }}
        >
          <div className="mht-coach-arrows" aria-hidden="true">
            <img src="/assets/images/hud/scroll-arrow.svg" alt="" />
            <img src="/assets/images/hud/scroll-arrow.svg" alt="" />
          </div>
          <div className="mht-coach-bubble">
            <strong>{t("common:resources.coachTitle")}</strong>
            <span>{t("common:resources.coachBody")}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResourceCoachMark;
